<template>
  <div
    class="trezo-card bg-white dark:bg-[#0c1427] mb-[25px] p-[20px] md:p-[25px] rounded-md"
  >
    <div class="trezo-card-header mb-[20px] md:mb-[25px] sm:flex items-center justify-between">
      <div class="trezo-card-title">
        <form class="relative sm:w-[265px]" @submit.prevent="fetchProducts">
          <label
            class="leading-none absolute ltr:left-[13px] rtl:right-[13px] text-black dark:text-white mt-px top-1/2 -translate-y-1/2"
          >
            <i class="material-symbols-outlined !text-[20px]"> search </i>
          </label>
          <input
            type="text"
            :placeholder="directT('ecommerce.productsList.searchHere')"
            class="bg-gray-50 border border-gray-50 h-[36px] text-xs rounded-md w-full block text-black pt-[11px] pb-[12px] ltr:pl-[38px] rtl:pr-[38px] ltr:pr-[13px] ltr:md:pr-[16px] rtl:pl-[13px] rtl:md:pl-[16px] placeholder:text-gray-500 outline-0 dark:bg-[#15203c] dark:text-white dark:border-[#15203c] dark:placeholder:text-gray-400"
            v-model="searchQuery"
            @input="fetchProducts"
          />
        </form>
      </div>
      <div class="trezo-card-subtitle mt-[15px] sm:mt-0" v-if="canAddProducts">
        <RouterLink
          to="/ecommerce/create-product"
          class="inline-block transition-all rounded-md font-medium px-[13px] py-[6px] text-primary-500 border border-primary-500 hover:bg-primary-500 hover:text-white"
          id="add-new-popup-toggle"
        >
          <span class="inline-block relative ltr:pl-[22px] rtl:pr-[22px]">
            <i
              class="material-symbols-outlined !text-[22px] absolute ltr:-left-[4px] rtl:-right-[4px] top-1/2 -translate-y-1/2"
            >
              add
            </i>
            {{ directT('ecommerce.productsList.addNewProduct') }}
          </span>
        </RouterLink>
      </div>
    </div>
    <div class="trezo-card-content">
      <div v-if="loading" class="flex justify-center py-8">
        <div class="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary-500 border-r-transparent"></div>
      </div>
      <div v-else-if="error" class="p-4 bg-red-100 text-red-700 rounded mb-4">
        {{ error }}
      </div>
      <div v-else-if="products.length === 0" class="text-center py-8 text-gray-500">
        {{ directT('ecommerce.productsList.noProductsFound') }}
      </div>
      <div v-else class="table-responsive overflow-x-auto">
        <table class="w-full">
          <thead class="text-black dark:text-white">
            <tr>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap ltr:first:rounded-tl-md ltr:last:rounded-tr-md rtl:first:rounded-tr-md rtl:last:rounded-tl-md"
              >
                {{ directT('ecommerce.productsList.id') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap ltr:first:rounded-tl-md ltr:last:rounded-tr-md rtl:first:rounded-tr-md rtl:last:rounded-tl-md"
              >
                {{ directT('ecommerce.productsList.product') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap ltr:first:rounded-tl-md ltr:last:rounded-tr-md rtl:first:rounded-tr-md rtl:last:rounded-tl-md"
              >
                {{ directT('ecommerce.productsList.category') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap ltr:first:rounded-tl-md ltr:last:rounded-tr-md rtl:first:rounded-tr-md rtl:last:rounded-tl-md"
              >
                {{ directT('ecommerce.productsList.price') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap ltr:first:rounded-tl-md ltr:last:rounded-tr-md rtl:first:rounded-tr-md rtl:last:rounded-tl-md"
              >
                {{ directT('ecommerce.productsList.amount') }}
              </th>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap ltr:first:rounded-tr-md ltr:last:rounded-tr-md rtl:first:rounded-tr-md rtl:last:rounded-tl-md"
              >
                {{ directT('ecommerce.productsList.actions') }}
              </th>
            </tr>
          </thead>
          <tbody class="text-black dark:text-white">
            <tr v-for="product in products" :key="product.id">
              <td
                class="text-gray-500 dark:text-gray-400 ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                {{ product.id.substring(0, 8) }}
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                <div class="flex items-center">
                  <div class="rounded-md w-[40px]">
                    <img
                      :src="product.image_link || require('@/assets/images/products/product1.jpg')"
                      class="inline-block rounded-md"
                      alt="product-image"
                    />
                  </div>
                  <div class="ltr:ml-[12px] rtl:mr-[12px]">
                    <RouterLink
                      :to="`/ecommerce/edit-product?id=${product.id}`"
                      class="font-medium inline-block text-[14px] md:text-[15px] transition-all hover:text-primary-500 mb-px"
                    >
                      {{ product.title }}
                    </RouterLink>
                    <span
                      class="block text-sm text-gray-500 dark:text-gray-400"
                    >
                      {{ product.published_at ? new Date(product.published_at).toLocaleDateString() : 'Draft' }}
                    </span>
                  </div>
                </div>
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                {{ product.category || 'Uncategorized' }}
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                {{ formatPrice(product.price) }}
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                {{ formatPrice(product.compare_at_price) }}
              </td>
              <td
                class="ltr:text-left rtl:text-right whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                <div class="flex items-center gap-[9px]">
                  <RouterLink
                    :to="`/ecommerce/edit-product?id=${product.id}`"
                    class="text-primary-500 leading-none custom-tooltip"
                    v-tooltip="directT('ecommerce.productsList.edit')"
                  >
                    <i class="material-symbols-outlined !text-md">
                      edit
                    </i>
                  </RouterLink>
                  <button
                    type="button"
                    class="text-danger-500 leading-none custom-tooltip"
                    v-tooltip="directT('ecommerce.productsList.delete')"
                  >
                    <i class="material-symbols-outlined !text-md">
                      delete
                    </i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from "vue";
import { formatCurrency, getUserCurrency } from "@/utils/currency";
import { supabase } from "@/lib/supabaseClient";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

interface Product {
  id: string;
  title: string;
  published_at: string;
  category: string;
  price: number;
  compare_at_price: number;
  image_link: string;
}

// Type definition for translation objects
type TranslationObject = {
  [key: string]: string | TranslationObject;
};

export default defineComponent({
  name: "ProductsList",
  setup() {
    const activeTab = ref("allProducts");
    const currencyCode = ref('ILS'); // ברירת מחדל
    const products = ref<Product[]>([]);
    const loading = ref(true);
    const error = ref<string | null>(null);
    const searchQuery = ref("");
    const organization = ref<any>(null);

    // i18n setup
    const { locale } = useI18n();

    // Translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string): string => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return typeof result === 'string' ? result : key;
    };

    // Computed property to check if user can add products
    const canAddProducts = computed(() => {
      return organization.value?.ecommerce === true;
    });

    // Fetch organization data for the current user
    const fetchOrganization = async () => {
      try {
        // Get current user from Supabase auth
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Get the user's organization
        const { data: userData, error: userError } = await supabase
          .from('user_profiles')
          .select('organization_id')
          .eq('user_id', user.id)
          .single();

        if (userError || !userData?.organization_id) {
          console.error('Error fetching user organization:', userError);
          return;
        }

        // Get organization details
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select('*')
          .eq('id', userData.organization_id)
          .single();

        if (orgError) {
          console.error('Error fetching organization:', orgError);
          return;
        }

        organization.value = orgData;
      } catch (err) {
        console.error('Unexpected error fetching organization:', err);
      }
    };

    // Load currency code and products when component mounts
    onMounted(async () => {
      try {
        // Get user's currency
        currencyCode.value = await getUserCurrency();
        await fetchOrganization();
        fetchProducts();
      } catch (error) {
        console.error('Error during initialization:', error);
      }
    });

    const fetchProducts = async () => {
      try {
        loading.value = true;
        error.value = null;

        let query = supabase.from("products").select("*");

        // Filter by search query if provided
        if (searchQuery.value) {
          query = query.ilike("title", `%${searchQuery.value}%`);
        }

        const { data, error: fetchError } = await query;

        if (fetchError) {
          throw fetchError;
        }

        // Cast the data to the Product type
        products.value = (data || []).map(item => ({
          id: String(item.id || ''),
          title: String(item.title || ''),
          published_at: String(item.published_at || ''),
          category: String(item.category || ''),
          price: typeof item.price === 'number' ? item.price : 0,
          compare_at_price: typeof item.compare_at_price === 'number' ? item.compare_at_price : 0,
          image_link: String(item.image_link || '')
        }));
        
        loading.value = false;
      } catch (err: any) {
        console.error("Error loading products:", err);
        error.value = directT('ecommerce.productsList.errorLoadingProducts') + `: ${err.message || directT('ecommerce.productsList.unknownError')}`;
        loading.value = false;
      }
    };

    const formatPrice = (price: number | null) => {
      if (!price) return "₪0.00";
      return formatCurrency(price, currencyCode.value);
    };

    return {
      products,
      loading,
      error,
      searchQuery,
      formatPrice,
      fetchProducts,
      canAddProducts,
      directT
    };
  },
});
</script>
